import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createAdminCategory, fetchAdminCategories, updateAdminCategory, updateAdminCategoryStatus } from "@/admin/api";
import type { AdminCategory } from "@/admin/types";
import LanguageSelect from "@/components/feature/LanguageSelect";
import MediaPicker, { getTablerIconByName } from "@/components/feature/MediaPicker";
import Modal from "@/components/ui/Modal";
import IconRenderer from "@/components/feature/IconRenderer";
import AdminListCard from "@/pages/admin/components/AdminListCard";

type FormState = {
  colorHex: string;
  iconUrl: string;
  translations: Record<string, string>;
};

const isImageUrl = (value: string) => {
  const lower = value.toLowerCase();
  return lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".gif") || lower.endsWith(".webp") || lower.endsWith(".svg");
};

const renderIconPreview = (value: string) => {
  return <IconRenderer iconUrl={value} size={18} />;
};

export default function AdminCategories() {
  const { t, i18n } = useTranslation("common");
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState("");

  const languages = useMemo(() => Object.keys(i18n.options.resources || {}), [i18n.options.resources]);
  const currentLanguage = useMemo(() => (i18n.language || "ar").split("-")[0], [i18n.language]);
  const defaultLanguage = useMemo(() => {
    const fallback = i18n.options.fallbackLng;
    if (typeof fallback === "string") return fallback;
    if (Array.isArray(fallback)) return fallback[0] || "ar";
    if (fallback && typeof fallback === "object") {
      const entry = Object.values(fallback)[0];
      if (Array.isArray(entry)) return entry[0] || "ar";
      if (typeof entry === "string") return entry;
    }
    return "ar";
  }, [i18n.options.fallbackLng]);

  const buildTranslations = (category?: AdminCategory) => {
    return languages.reduce<Record<string, string>>((acc, lng) => {
      const existing = category?.translations.find((item) => item.locale === lng);
      acc[lng] = existing?.name || "";
      return acc;
    }, {});
  };

  const getCategoryName = (category: AdminCategory) => {
    const direct = category.translations.find((item) => item.locale === currentLanguage)?.name;
    if (direct) return direct;
    const fallback = category.translations.find((item) => item.locale === defaultLanguage)?.name;
    if (fallback) return fallback;
    return category.translations[0]?.name || category.key;
  };

  const [form, setForm] = useState<FormState>({
    colorHex: "",
    iconUrl: "",
    translations: buildTranslations(),
  });
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

  const loadCategories = () => {
    return fetchAdminCategories()
      .then((data) => setCategories(data))
      .catch(() => setCategories([]));
  };

  useEffect(() => {
    let isMounted = true;
    loadCategories().finally(() => {
      if (!isMounted) return;
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      translations: buildTranslations(editingId ? categories.find((c) => c.id === editingId) : undefined),
    }));
  }, [languages, editingId, categories]);

  const resetForm = () => {
    setEditingId(null);
    setForm({ colorHex: "", iconUrl: "", translations: buildTranslations() });
    setSelectedLanguage(defaultLanguage);
    setError("");
  };

  const handleEdit = (category: AdminCategory) => {
    setEditingId(category.id);
    setForm({
      colorHex: category.colorHex,
      iconUrl: category.iconUrl || "",
      translations: buildTranslations(category),
    });
    setSelectedLanguage(defaultLanguage);
    setError("");
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    setForm((prev) => {
      const current = prev.translations[language];
      if (current && current.trim().length > 0) {
        return prev;
      }
      const fallbackValue = prev.translations[defaultLanguage];
      if (!fallbackValue || fallbackValue.trim().length === 0) {
        return prev;
      }
      return {
        ...prev,
        translations: {
          ...prev.translations,
          [language]: fallbackValue,
        },
      };
    });
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.colorHex.trim()) {
      setError(t("adminFillRequired"));
      return;
    }

    const translations = languages
      .map((lng) => ({ locale: lng, name: form.translations[lng]?.trim() || "" }))
      .filter((item) => item.name.length > 0);

    if (translations.length === 0) {
      setError(t("adminFillTranslations"));
      return;
    }

    setIsSaving(true);
    try {
      if (editingId) {
        await updateAdminCategory(editingId, {
          colorHex: form.colorHex.trim(),
          iconUrl: form.iconUrl.trim() || null,
          translations,
        });
      } else {
        await createAdminCategory({
          colorHex: form.colorHex.trim(),
          iconUrl: form.iconUrl.trim() || null,
          translations,
        });
      }
      await loadCategories();
      resetForm();
      setIsModalOpen(false);
    } catch {
      setError(t("adminSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStatus = async (category: AdminCategory) => {
    await updateAdminCategoryStatus(category.id, !category.isActive);
    await loadCategories();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">{t("adminCategories")}</h2>
          <p className="text-sm text-gray-500 mt-1">{t("adminCategoryManageHint")}</p>
        </div>
        <button
          onClick={handleCreate}
          className="px-5 py-2.5 rounded-xl bg-gradient-to-l from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-md transition-all flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t("adminAddCategory")}
        </button>
      </div>

      {/* Desktop Grid View */}
      <div className="hidden md:block bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800/80 overflow-hidden shadow-sm">
        <div className="overflow-x-auto custom-scrollbar">
          <div className="min-w-[800px] divide-y divide-gray-50 dark:divide-zinc-800/50">
            <div className="grid grid-cols-5 gap-4 px-6 py-4 bg-gray-50/50 dark:bg-zinc-800/30 text-xs font-semibold text-gray-500">
              <span>{t("adminCategoryName")}</span>
              <span>{t("adminIconPreview")}</span>
              <span>{t("adminCategoryColor")}</span>
              <span>{t("adminStatus")}</span>
              <span className="text-left">{t("adminActions")}</span>
            </div>
            {categories.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-50 dark:bg-zinc-800/60 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <p className="text-gray-500 dark:text-zinc-400">{t("adminNoData")}</p>
              </div>
            ) : (
              categories.map((category) => (
                <div key={category.id} className="grid grid-cols-5 gap-4 px-6 py-4 text-sm items-center hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                  <span className="text-gray-800 dark:text-zinc-200 font-medium">{getCategoryName(category)}</span>
                  <span className="text-gray-700 flex items-center">
                    <div className="p-2 bg-gray-50 dark:bg-zinc-800/50 rounded-lg border border-gray-100 dark:border-zinc-800 shadow-sm">{renderIconPreview(category.iconUrl)}</div>
                  </span>
                  <span className="flex items-center gap-2 text-gray-700 dark:text-zinc-300 font-mono text-xs bg-gray-50 dark:bg-zinc-800/50 px-2.5 py-1.5 rounded-lg border border-gray-100 dark:border-zinc-800 w-max">
                    <span className="w-3 h-3 rounded-full border border-gray-200" style={{ backgroundColor: category.colorHex }} />
                    {category.colorHex}
                  </span>
                  <div>
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                      category.isActive ? "bg-emerald-100 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400" : "bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400"
                    }`}>
                      {category.isActive ? t("adminActive") : t("adminInactive")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 justify-start" dir="ltr">
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                      title={t("adminEdit")}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => toggleStatus(category)}
                      className={`p-2 rounded-lg transition-colors ${
                        category.isActive ? "text-amber-500 hover:bg-amber-50 dark:hover:bg-zinc-800" : "text-emerald-500 hover:bg-emerald-50 dark:hover:bg-zinc-800"
                      }`}
                      title={category.isActive ? t("adminDisable") : t("adminEnable")}
                    >
                      {category.isActive ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Mobile Card List View */}
      <div className="block md:hidden space-y-4">
        {categories.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800/85 p-12 text-center shadow-sm">
            <div className="w-16 h-16 bg-gray-50 dark:bg-zinc-800/60 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-zinc-400">{t("adminNoData")}</p>
          </div>
        ) : (
          categories.map((category) => (
            <AdminListCard
              key={category.id}
              title={getCategoryName(category)}
              iconUrl={category.iconUrl}
              iconFallback="ri-stack-line"
              isActive={category.isActive}
              onEdit={() => handleEdit(category)}
              onToggleStatus={() => toggleStatus(category)}
              footerLeft={
                <span className="flex items-center gap-2 text-gray-650 dark:text-zinc-350 font-mono text-xs bg-gray-50 dark:bg-zinc-800/50 px-2.5 py-1.5 rounded-lg border border-gray-100 dark:border-zinc-800 w-max">
                  <span className="w-3.5 h-3.5 rounded-full border border-gray-200 dark:border-zinc-700" style={{ backgroundColor: category.colorHex }} />
                  {category.colorHex}
                </span>
              }
            />
          ))
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingId ? t("adminEditCategory") : t("adminAddCategory")}
        maxWidth="3xl"
        footer={
          <>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
            >
              {t("adminCancel")}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              {editingId ? t("adminUpdate") : t("adminCreate")}
            </button>
          </>
        }
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminCategoryColor")}</label>
              <div className="flex items-center gap-3">
                <div className="relative w-12 h-12 rounded-xl overflow-hidden border border-gray-200 shadow-sm flex-shrink-0">
                  <input
                    type="color"
                    value={form.colorHex || "#E8927C"}
                    onChange={(event) => setForm((prev) => ({ ...prev, colorHex: event.target.value }))}
                    className="absolute -inset-4 w-20 h-20 cursor-pointer"
                  />
                </div>
                <input
                  value={form.colorHex}
                  onChange={(event) => setForm((prev) => ({ ...prev, colorHex: event.target.value }))}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all font-mono uppercase"
                  placeholder="#000000"
                />
              </div>
            </div>

            <div className="col-span-1 md:col-span-2">
              <MediaPicker
                label={t("adminCategoryIcon")}
                value={form.iconUrl}
                onChange={(nextValue) => setForm((prev) => ({ ...prev, iconUrl: nextValue }))}
              />
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <label className="block text-xs font-semibold text-gray-600 mb-3">{t("adminTranslationValue")}</label>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="w-full sm:w-32 flex-shrink-0">
                <LanguageSelect languages={languages} value={selectedLanguage} onChange={handleLanguageChange} />
              </div>
              <input
                value={form.translations[selectedLanguage] || ""}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    translations: { ...prev.translations, [selectedLanguage]: event.target.value },
                  }))
                }
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                placeholder={t("adminTranslationHint")}
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600 flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {error}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
